import { getMessaging, getToken, deleteToken, isSupported, onMessage } from 'firebase/messaging';
import { doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { app, auth, db } from '../firebase';
import { readJSON, writeJSON } from './deviceStore';

/*
 * Push notifications for this device, through Firebase Cloud Messaging and the app's own
 * service worker (src/service-worker.js). A device that says yes stores its token in
 * push_tokens/{token} with the profile's role, site and division, which is how the sender
 * picks recipients without ever addressing a person. The token is refreshed on each start
 * and whenever the profile changes.
 *
 * iPhone: web push works only once the app is on the home screen (iOS 16.4+), and the
 * permission dialog must follow a tap. Android and desktop: anywhere.
 */

/** Public web push key from the Firebase console (Project settings > Cloud Messaging > Web Push certificates). Public by design. */
export const VAPID_KEY = 'BBn1vyQXYNOg5jkqqNqkHD_V-Qazq2ngEBBBs1ma6jyJIGK6ByWlFMj-mP0OjKywMNHJGRNNpNksIr5vNAHhbMo';

const TOKEN_KEY = 'push-token'; // the token this device registered, so it can be refreshed or removed
const NUDGE_KEY = 'push-nudge-dismissed';

export const isInstalled = () => (typeof window !== 'undefined') && Boolean((window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || window.navigator.standalone === true);
export const isIOS = () => typeof navigator !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;
export const pushConfigured = () => Boolean(VAPID_KEY);

/** 'unsupported' | 'install-first' (iPhone, not on home screen) | 'default' | 'granted' | 'denied' */
export const pushState = () => {
  if (!pushConfigured()) return 'unsupported';
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return 'unsupported';
  if (!('Notification' in window) || !('PushManager' in window)) return isIOS() && !isInstalled() ? 'install-first' : 'unsupported';
  return Notification.permission; // default | granted | denied
};

export const nudgeDismissed = () => Boolean(readJSON(NUDGE_KEY, false));
export const dismissNudge = () => { try { writeJSON(NUDGE_KEY, true); } catch { /* ignore */ } };

const platform = () => (isIOS() ? 'ios' : /Android/.test(navigator.userAgent) ? 'android' : 'desktop');

const tokenDoc = async (token, profile) => {
  const user = auth.currentUser;
  if (!user) return;
  await setDoc(doc(db, 'push_tokens', token), {
    uid: user.uid,
    role: (profile && profile.role) || null,
    site: profile ? (profile.role === 'resident' ? profile.rotation || null : profile.site || null) : null,
    division: (profile && profile.division) || null,
    gradYear: (profile && profile.gradYear) || null,
    platform: platform(),
    installed: isInstalled(),
    origin: window.location.origin,
    updatedAt: serverTimestamp(),
  });
};

const messagingFor = async () => {
  if (!(await isSupported())) return null;
  return getMessaging(app);
};

/** Ask for permission (must follow a tap) and register this device. Returns the resulting state. */
export const enablePush = async (profile) => {
  const state = pushState();
  if (state === 'unsupported' || state === 'install-first') return state;
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission;
  await refreshPushToken(profile);
  return 'granted';
};

/** Re-register the current token with the current profile. Safe to call often; no prompt. */
export const refreshPushToken = async (profile) => {
  if (pushState() !== 'granted' || !auth.currentUser) return;
  try {
    const messaging = await messagingFor();
    if (!messaging) return;
    const registration = await navigator.serviceWorker.ready;
    const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
    if (!token) return;
    const previous = readJSON(TOKEN_KEY, null);
    if (previous && previous !== token) { try { await deleteDoc(doc(db, 'push_tokens', previous)); } catch { /* ignore */ } }
    await tokenDoc(token, profile);
    writeJSON(TOKEN_KEY, token);
  } catch (e) { console.warn('[push] token refresh failed', e.message); }
};

/** Stop notifications on this device (the browser permission stays; the token goes) */
export const disablePush = async () => {
  try {
    const previous = readJSON(TOKEN_KEY, null);
    const messaging = await messagingFor();
    if (messaging) await deleteToken(messaging);
    if (previous) await deleteDoc(doc(db, 'push_tokens', previous));
  } catch (e) { console.warn('[push] disable failed', e.message); }
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
};

/** True when this device has a registered token (so the switch shows On) */
export const pushEnabledHere = () => pushState() === 'granted' && Boolean(readJSON(TOKEN_KEY, null));

/** A message arriving while the app is open in the foreground */
export const onForegroundPush = async (cb) => {
  const messaging = await messagingFor().catch(() => null);
  if (!messaging) return () => {};
  return onMessage(messaging, cb);
};
